/**
 * Messenger Chat Component
 *
 * Chat view for individual conversations with:
 * - Message list with reactions
 * - Message input with send button
 * - Typing indicators
 * - File attachment support (in expanded/full modes)
 */

import { LitElement, html, css } from 'lit';
import { customElement, property, state, query } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { messengerAPI } from '../services/messenger-api';
import { messengerWsService } from '../services/messenger-websocket';
import { eventBus, Events } from '../services/events';
import type { Message, Conversation, MessengerWidgetMode, TypingIndicator } from '../types/messenger';

@customElement('moya-messenger-chat')
export class MoyaMessengerChat extends LitElement {
  static styles = css`
    :host {
      display: flex;
      flex-direction: column;
      height: 100%;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    }

    .messages-container {
      flex: 1;
      overflow-y: auto;
      padding: 16px;
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .message {
      display: flex;
      gap: 8px;
      max-width: 85%;
    }

    .message.outgoing {
      flex-direction: row-reverse;
      align-self: flex-end;
    }

    .message.incoming {
      align-self: flex-start;
    }

    .message-avatar {
      width: 28px;
      height: 28px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 11px;
      font-weight: 600;
      color: white;
      flex-shrink: 0;
    }

    .message-avatar img {
      width: 100%;
      height: 100%;
      border-radius: 50%;
      object-fit: cover;
    }

    .message-content {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .message-sender {
      font-size: 11px;
      font-weight: 500;
      color: rgba(255, 255, 255, 0.5);
    }

    .message.outgoing .message-sender {
      text-align: right;
    }

    .message-bubble {
      padding: 8px 12px;
      border-radius: 12px;
      font-size: 13px;
      line-height: 1.4;
      word-wrap: break-word;
    }

    .message.incoming .message-bubble {
      background: rgba(255, 255, 255, 0.1);
      color: white;
      border-bottom-left-radius: 4px;
    }

    .message.outgoing .message-bubble {
      background: #3b82f6;
      color: white;
      border-bottom-right-radius: 4px;
    }

    .message-time {
      font-size: 10px;
      color: rgba(255, 255, 255, 0.4);
    }

    .message.outgoing .message-time {
      text-align: right;
    }

    /* Reactions */
    .message-reactions {
      display: flex;
      gap: 4px;
      margin-top: 4px;
      flex-wrap: wrap;
    }

    .reaction {
      display: flex;
      align-items: center;
      gap: 2px;
      padding: 2px 6px;
      background: rgba(255, 255, 255, 0.1);
      border-radius: 10px;
      font-size: 12px;
      cursor: pointer;
      transition: background-color 0.15s ease;
    }

    .reaction:hover {
      background: rgba(255, 255, 255, 0.15);
    }

    .reaction-count {
      font-size: 10px;
      color: rgba(255, 255, 255, 0.6);
    }

    /* Typing indicator */
    .typing-indicator {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 16px;
      font-size: 12px;
      color: rgba(255, 255, 255, 0.5);
    }

    .typing-dots {
      display: flex;
      gap: 3px;
    }

    .typing-dot {
      width: 6px;
      height: 6px;
      background: rgba(255, 255, 255, 0.5);
      border-radius: 50%;
      animation: typing 1.4s infinite;
    }

    .typing-dot:nth-child(2) {
      animation-delay: 0.2s;
    }

    .typing-dot:nth-child(3) {
      animation-delay: 0.4s;
    }

    @keyframes typing {
      0%, 60%, 100% {
        transform: translateY(0);
        opacity: 0.5;
      }
      30% {
        transform: translateY(-4px);
        opacity: 1;
      }
    }

    /* Input area */
    .input-area {
      display: flex;
      align-items: flex-end;
      gap: 8px;
      padding: 12px 16px;
      border-top: 1px solid rgba(255, 255, 255, 0.1);
      background: rgba(0, 0, 0, 0.2);
    }

    .input-wrapper {
      flex: 1;
      display: flex;
      align-items: flex-end;
      gap: 8px;
      background: rgba(255, 255, 255, 0.08);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 20px;
      padding: 8px 12px;
      transition: all 0.15s ease;
    }

    .input-wrapper:focus-within {
      background: rgba(255, 255, 255, 0.1);
      border-color: rgba(59, 130, 246, 0.5);
    }

    .message-input {
      flex: 1;
      border: none;
      background: transparent;
      color: white;
      font-size: 13px;
      outline: none;
      resize: none;
      max-height: 120px;
      line-height: 1.4;
    }

    .message-input::placeholder {
      color: rgba(255, 255, 255, 0.4);
    }

    .attach-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      border: none;
      background: transparent;
      border-radius: 50%;
      cursor: pointer;
      color: rgba(255, 255, 255, 0.5);
      transition: all 0.15s ease;
      flex-shrink: 0;
    }

    .attach-btn:hover {
      color: white;
      background: rgba(255, 255, 255, 0.1);
    }

    .attach-btn svg {
      width: 18px;
      height: 18px;
    }

    .send-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 36px;
      border: none;
      background: #3b82f6;
      border-radius: 50%;
      cursor: pointer;
      color: white;
      transition: all 0.15s ease;
      flex-shrink: 0;
    }

    .send-btn:hover:not(:disabled) {
      background: #2563eb;
    }

    .send-btn:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .send-btn svg {
      width: 18px;
      height: 18px;
    }

    /* Loading */
    .loading-messages {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 100%;
    }

    .spinner {
      width: 24px;
      height: 24px;
      border: 2px solid rgba(255, 255, 255, 0.1);
      border-top-color: #3b82f6;
      border-radius: 50%;
      animation: spin 0.8s linear infinite;
    }

    @keyframes spin {
      to {
        transform: rotate(360deg);
      }
    }

    /* Date separator */
    .date-separator {
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 16px 0;
    }

    .date-separator span {
      padding: 4px 12px;
      background: rgba(255, 255, 255, 0.1);
      border-radius: 10px;
      font-size: 11px;
      color: rgba(255, 255, 255, 0.5);
    }

    /* System message */
    .system-message {
      text-align: center;
      padding: 8px 16px;
      font-size: 12px;
      color: rgba(255, 255, 255, 0.5);
      font-style: italic;
    }
  `;

  @property({ type: Object }) conversation: Conversation | null = null;
  @property({ type: String }) token = '';
  @property({ type: String }) mode: MessengerWidgetMode = 'mini';
  @property({ type: String }) userId = '';

  @state() private messages: Message[] = [];
  @state() private loading = true;
  @state() private sending = false;
  @state() private messageText = '';
  @state() private typingUsers: Map<string, string> = new Map();

  @query('.message-input') private messageInput!: HTMLTextAreaElement;
  @query('.messages-container') private messagesContainer!: HTMLElement;

  private unsubscribers: (() => void)[] = [];
  private typingTimer: ReturnType<typeof setTimeout> | null = null;
  private isTyping = false;

  connectedCallback() {
    super.connectedCallback();
    this.setupEventListeners();
    if (this.conversation) {
      this.loadMessages();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.unsubscribers.forEach((unsub) => unsub());
    this.unsubscribers = [];
    if (this.typingTimer) {
      clearTimeout(this.typingTimer);
    }
  }

  updated(changedProperties: Map<string, unknown>) {
    if (changedProperties.has('conversation') && this.conversation) {
      this.loadMessages();
    }
  }

  private setupEventListeners() {
    // Listen for new messages
    this.unsubscribers.push(
      eventBus.on(Events.MESSENGER_MESSAGE_RECEIVED, (message: unknown) => {
        const msg = message as Message;
        if (msg.conversationId === this.conversation?.id) {
          this.messages = [...this.messages, msg];
          this.scrollToBottom();
        }
      })
    );

    // Listen for message updates
    this.unsubscribers.push(
      eventBus.on('messenger:message:updated', (message: unknown) => {
        const msg = message as Message;
        if (msg.conversationId === this.conversation?.id) {
          const index = this.messages.findIndex((m) => m.id === msg.id);
          if (index >= 0) {
            this.messages = [
              ...this.messages.slice(0, index),
              msg,
              ...this.messages.slice(index + 1),
            ];
          }
        }
      })
    );

    // Listen for typing indicators
    this.unsubscribers.push(
      eventBus.on(Events.MESSENGER_TYPING, (data: unknown) => {
        const { conversationId, userId, userName, isTyping } = data as TypingIndicator & {
          isTyping: boolean;
        };
        if (conversationId === this.conversation?.id) {
          if (isTyping) {
            this.typingUsers = new Map(this.typingUsers).set(userId, userName);
          } else {
            const newMap = new Map(this.typingUsers);
            newMap.delete(userId);
            this.typingUsers = newMap;
          }
        }
      })
    );
  }

  private async loadMessages() {
    if (!this.conversation) return;

    this.loading = true;
    try {
      const response = await messengerAPI.getMessages({
        conversationId: this.conversation.id,
        limit: 50,
      });
      const messages = response?.messages;
      this.messages = Array.isArray(messages) ? messages.reverse() : []; // API returns newest first
      this.scrollToBottom();
    } catch (e) {
      console.error('[MessengerChat] Failed to load messages:', e);
    } finally {
      this.loading = false;
    }
  }

  private scrollToBottom() {
    requestAnimationFrame(() => {
      if (this.messagesContainer) {
        this.messagesContainer.scrollTop = this.messagesContainer.scrollHeight;
      }
    });
  }

  private handleInput(e: Event) {
    const textarea = e.target as HTMLTextAreaElement;
    this.messageText = textarea.value;

    // Auto-resize textarea
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;

    // Send typing indicator
    this.sendTypingIndicator();
  }

  private sendTypingIndicator() {
    if (!this.conversation) return;

    if (!this.isTyping) {
      this.isTyping = true;
      messengerWsService.sendTypingStart(this.conversation.id);
    }

    // Reset timer
    if (this.typingTimer) {
      clearTimeout(this.typingTimer);
    }

    this.typingTimer = setTimeout(() => {
      this.isTyping = false;
      if (this.conversation) {
        messengerWsService.sendTypingStop(this.conversation.id);
      }
    }, 2000);
  }

  private handleKeyDown(e: KeyboardEvent) {
    // Send on Enter (without Shift)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      this.sendMessage();
    }
  }

  private async sendMessage() {
    if (!this.conversation || !this.messageText.trim() || this.sending) return;

    const content = this.messageText.trim();
    this.messageText = '';
    this.sending = true;

    // Reset textarea height
    if (this.messageInput) {
      this.messageInput.style.height = 'auto';
    }

    // Stop typing indicator
    if (this.typingTimer) {
      clearTimeout(this.typingTimer);
    }
    if (this.isTyping && this.conversation) {
      this.isTyping = false;
      messengerWsService.sendTypingStop(this.conversation.id);
    }

    try {
      const message = await messengerAPI.sendMessage({
        conversationId: this.conversation.id,
        content,
        contentType: 'text',
      });
      this.messages = [...this.messages, message];
      this.scrollToBottom();
    } catch (e) {
      console.error('[MessengerChat] Failed to send message:', e);
      // Restore message text on failure
      this.messageText = content;
    } finally {
      this.sending = false;
    }
  }

  private getAvatarColor(name: string): string {
    const colors = [
      'linear-gradient(135deg, #8b5cf6, #ec4899)',
      'linear-gradient(135deg, #3b82f6, #06b6d4)',
      'linear-gradient(135deg, #10b981, #3b82f6)',
      'linear-gradient(135deg, #f59e0b, #ef4444)',
      'linear-gradient(135deg, #ec4899, #8b5cf6)',
    ];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  }

  private getInitials(name: string): string {
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  }

  private formatTime(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  private isOutgoing(message: Message): boolean {
    return message.senderId === this.userId;
  }

  private renderMessage(message: Message, showSender: boolean) {
    const isOutgoing = this.isOutgoing(message);
    const messageClasses = classMap({
      message: true,
      outgoing: isOutgoing,
      incoming: !isOutgoing,
    });

    if (message.contentType === 'system') {
      return html`<div class="system-message">${message.content}</div>`;
    }

    return html`
      <div class="${messageClasses}">
        ${!isOutgoing
          ? html`
              <div
                class="message-avatar"
                style="background: ${this.getAvatarColor(message.senderName)}"
              >
                ${message.senderAvatarUrl
                  ? html`<img src="${message.senderAvatarUrl}" alt="${message.senderName}" />`
                  : this.getInitials(message.senderName)}
              </div>
            `
          : ''}
        <div class="message-content">
          ${showSender && !isOutgoing
            ? html`<div class="message-sender">${message.senderName}</div>`
            : ''}
          <div class="message-bubble">${message.content}</div>
          ${message.reactions && message.reactions.length > 0
            ? html`
                <div class="message-reactions">
                  ${this.groupReactions(message.reactions).map(
                    ([emoji, count]) => html`
                      <span class="reaction">
                        ${emoji}
                        ${count > 1 ? html`<span class="reaction-count">${count}</span>` : ''}
                      </span>
                    `
                  )}
                </div>
              `
            : ''}
          <div class="message-time">${this.formatTime(message.createdAt)}</div>
        </div>
      </div>
    `;
  }

  private groupReactions(reactions: Message['reactions']): [string, number][] {
    if (!reactions) return [];
    const counts = new Map<string, number>();
    reactions.forEach((r) => {
      counts.set(r.emoji, (counts.get(r.emoji) || 0) + 1);
    });
    return Array.from(counts.entries());
  }

  private renderTypingIndicator() {
    if (this.typingUsers.size === 0) return '';

    const names = Array.from(this.typingUsers.values());
    const text =
      names.length === 1
        ? `${names[0]} is typing`
        : names.length === 2
          ? `${names[0]} and ${names[1]} are typing`
          : `${names[0]} and ${names.length - 1} others are typing`;

    return html`
      <div class="typing-indicator">
        <div class="typing-dots">
          <span class="typing-dot"></span>
          <span class="typing-dot"></span>
          <span class="typing-dot"></span>
        </div>
        <span>${text}</span>
      </div>
    `;
  }

  render() {
    if (this.loading) {
      return html`
        <div class="loading-messages">
          <div class="spinner"></div>
        </div>
      `;
    }

    return html`
      <div class="messages-container">
        ${this.messages.map((message, index) => {
          const prevMessage = index > 0 ? this.messages[index - 1] : null;
          const showSender = !prevMessage || prevMessage.senderId !== message.senderId;
          return this.renderMessage(message, showSender);
        })}
        ${this.renderTypingIndicator()}
      </div>

      <div class="input-area">
        <div class="input-wrapper">
          ${this.mode !== 'mini'
            ? html`
                <button class="attach-btn" aria-label="Attach file">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </button>
              `
            : ''}
          <textarea
            class="message-input"
            placeholder="Type a message..."
            rows="1"
            .value="${this.messageText}"
            @input="${this.handleInput}"
            @keydown="${this.handleKeyDown}"
          ></textarea>
        </div>
        <button
          class="send-btn"
          @click="${this.sendMessage}"
          ?disabled="${!this.messageText.trim() || this.sending}"
          aria-label="Send message"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'moya-messenger-chat': MoyaMessengerChat;
  }
}
